import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

// 引入安全儲存工具
import { secureStorage } from "@/infrastructure/storage/secureStorage";
import { STORAGE_KEYS } from "@/domain/constants/storage";

type SidebarMode = "docked" | "overlay";

interface SidebarContextType {
  isOpen: boolean;
  mode: SidebarMode;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  isMobile: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

/**
 * Sidebar 狀態管理 Provider
 * 提供全局的側邊欄狀態管理，包括開關狀態、顯示模式和響應式調整
 */
export function SidebarProvider({ children }: SidebarProviderProps) {
  // 側邊欄是否開啟
  const [isOpen, setIsOpen] = useState(() => {
    // 從安全儲存中讀取狀態
    if (typeof window === "undefined") return true;

    const savedState = secureStorage.getItem(STORAGE_KEYS.SIDEBAR_OPEN, "true");
    // 預設為開啟，除非明確設為關閉
    return savedState !== "false";
  });

  // 側邊欄顯示模式：docked（固定）或 overlay（浮層）
  const [mode, setMode] = useState<SidebarMode>("docked");

  // 是否為行動裝置
  const [isMobile, setIsMobile] = useState(false);

  // 切換側邊欄開關狀態
  const toggleSidebar = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    // 保存狀態到安全儲存
    secureStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, String(newState));
  };

  // 開啟側邊欄
  const openSidebar = () => {
    setIsOpen(true);
    secureStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, "true");
  };

  // 關閉側邊欄
  const closeSidebar = () => {
    setIsOpen(false);
    secureStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, "false");
  };

  // 響應式調整
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // 在行動裝置上切換為浮層模式，在桌面裝置上切換為固定模式
      const newMode = mobile ? "overlay" : "docked";
      setMode(newMode);

      // 如果是行動裝置且切換到浮層模式，關閉側邊欄
      if (newMode === "overlay") {
        setIsOpen(false);
        secureStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, "false");
      } else if (newMode === "docked") {
        // 如果切換到固定模式，根據儲存的狀態決定是否開啟
        const savedState = secureStorage.getItem(
          STORAGE_KEYS.SIDEBAR_OPEN,
          "true",
        );
        setIsOpen(savedState !== "false");
      }
    };

    // 初始調用和設置事件監聽器
    handleResize();
    window.addEventListener("resize", handleResize);

    // 清理事件監聽器
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        mode,
        toggleSidebar,
        openSidebar,
        closeSidebar,
        isMobile,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

/**
 * 使用 Sidebar 狀態的 Hook
 * 提供對側邊欄狀態和控制函數的訪問
 */
export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
